
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useTeacherAvailabilityExceptions } from '@/hooks/useTeacherAvailabilityExceptions';
import { TIME_SLOTS } from '@/types/teacherAvailability';

interface ExceptionFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  teacherCode: string;
  selectedDate?: string;
  onSuccess?: () => void;
}

export function ExceptionFormDialog({ 
  isOpen, 
  onClose, 
  teacherCode, 
  selectedDate,
  onSuccess 
}: ExceptionFormDialogProps) {
  const { addException } = useTeacherAvailabilityExceptions(teacherCode);
  const [formData, setFormData] = useState({
    exceptionDate: selectedDate || '',
    exceptionType: 'NO_WORK' as 'NO_WORK' | 'PARTIAL_UNAVAILABLE',
    startTime: '',
    endTime: '',
    reason: ''
  });

  const resetForm = () => {
    setFormData({
      exceptionDate: selectedDate || '',
      exceptionType: 'NO_WORK',
      startTime: '',
      endTime: '',
      reason: ''
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const exceptionData = {
      exceptionDate: formData.exceptionDate,
      exceptionType: formData.exceptionType,
      startTime: formData.exceptionType === 'PARTIAL_UNAVAILABLE' ? formData.startTime : undefined,
      endTime: formData.exceptionType === 'PARTIAL_UNAVAILABLE' ? formData.endTime : undefined,
      reason: formData.reason || undefined
    };

    const success = await addException(exceptionData);
    if (success) {
      resetForm();
      if (onSuccess) {
        onSuccess();
      }
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>เพิ่มวันหยุดการสอน (ชั่วคราว)</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="exceptionDate">วันที่ *</Label>
            <Input
              id="exceptionDate"
              type="date"
              value={formData.exceptionDate}
              onChange={(e) => setFormData(prev => ({ ...prev, exceptionDate: e.target.value }))}
              required
            />
          </div>

          <div>
            <Label htmlFor="exceptionType">ประเภท *</Label>
            <Select
              value={formData.exceptionType}
              onValueChange={(value: 'NO_WORK' | 'PARTIAL_UNAVAILABLE') => 
                setFormData(prev => ({ ...prev, exceptionType: value }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NO_WORK">ไม่รับสอนทั้งวัน</SelectItem>
                <SelectItem value="PARTIAL_UNAVAILABLE">ไม่รับสอนบางช่วงเวลา</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.exceptionType === 'PARTIAL_UNAVAILABLE' && (
            <div className="flex space-x-2">
              <div className="flex-1">
                <Label htmlFor="startTime">เวลาเริ่ม</Label>
                <Select
                  value={formData.startTime}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, startTime: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="เลือกเวลา" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map(time => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label htmlFor="endTime">เวลาสิ้นสุด</Label>
                <Select
                  value={formData.endTime}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, endTime: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="เลือกเวลา" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map(time => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="reason">เหตุผล</Label>
            <Textarea
              id="reason"
              value={formData.reason}
              onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="ระบุเหตุผล (ถ้ามี)"
              rows={3}
            />
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
            >
              ยกเลิก
            </Button>
            <Button type="submit" className="flex-1">
              เพิ่ม
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
